import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./SuggestPriceExpeditor.module.scss";
import { Expeditor } from "../../../../../services";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import SubmitBidModal from "./SubmitBidModal";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import CustomTextarea from "../../../../custom/custom-textarea/CustomTextarea";
import { isTransitDaysRequired } from "../../utils/transitDaysValidation";
import { isRailwayOrder, MAX_RAILWAY_WAGONS_COUNT } from "../../../../../pages/orders/utils/railwayOrder";
import counterStyle from "../ComplexOrderBids/ComplexOrderBids.module.scss";

const SuggestPriceExpeditor = ({ onRefreshOrder, order, cargoDetails, onUpdateData, onReject }) => {
  const { t } = useTranslation();
  const isRailway = isRailwayOrder(order);
  const isWagonsCountRequired = isRailway && !order?.isComplexTender;
  const isWagonsCountLocked = isWagonsCountRequired && Number(order?.wagonsCount) > 0;
  const wagonsCountInputId = useId();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(order?.acceptedCurrency || "KZT");
  const lockCurrency = Boolean(order?.acceptedCurrency);
  const isAuction = order?.assignmentMethod === "Auction";
  const [volume, setVolume] = useState("");
  const [weight, setWeight] = useState("");
  const [enteredWagonsCount, setWagonsCount] = useState("");
  const wagonsCount = isWagonsCountLocked ? String(order.wagonsCount) : enteredWagonsCount;
  const [hasWagonsCountError, setHasWagonsCountError] = useState(false);
  const [transitDays, setTransitDays] = useState("");
  const [comments, setComments] = useState("");
  const [errorText, setErrorText] = useState("");
  const [transitDaysErrorText, setTransitDaysErrorText] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const initialVolume = useMemo(() => (cargoDetails?.volume != null ? String(cargoDetails.volume) : ""), [cargoDetails]);
  const initialWeight = useMemo(() => (cargoDetails?.weight != null ? String(cargoDetails.weight) : ""), [cargoDetails]);
  const weightUnit =
    cargoDetails?.unitMeasurement === "Kg"
      ? t("orderInfo.ordersInfoCargo.kgUnit")
      : t("orderInfo.ordersInfoCargo.tonsUnit");
  const transitDaysRequired = isTransitDaysRequired(order?.companyId);

  useEffect(() => {
    setPrice("");
    setVolume("");
    setWeight("");
    setWagonsCount("");
    setHasWagonsCountError(false);
    setTransitDays("");
    setComments("");
    setErrorText("");
    setTransitDaysErrorText("");
    setCurrency(order?.acceptedCurrency || "KZT");
  }, [order?.id, order?.acceptedCurrency]);

  const validate = () => {
    let isValid = true;
    const rounded = Math.round(Number(price));

    if (!rounded) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceRequired"));
      isValid = false;
    } else if (rounded <= 0) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceGreaterThanZero"));
      isValid = false;
    } else if (rounded > 2000000000) {
      setErrorText(t("orderInfoExpeditor.suggestPrice.priceTooLarge"));
      toast.error(t("orderInfoExpeditor.suggestPrice.priceTooLarge"));
      isValid = false;
    }

    if (transitDaysRequired && !transitDays.trim()) {
      setTransitDaysErrorText(t("orderInfoExpeditor.suggestPrice.transitDaysRequired"));
      isValid = false;
    }

    setHasWagonsCountError(false);
    if (isRailway) {
      const count = Number(wagonsCount);
      const errorKey =
        isWagonsCountRequired && (wagonsCount === "" || count === 0)
          ? "railwayOrder.wagonsCountRequired"
          : count > MAX_RAILWAY_WAGONS_COUNT
            ? "railwayOrder.wagonsCountTooLarge"
            : wagonsCount !== "" && (!Number.isInteger(count) || count < 0)
              ? "railwayOrder.invalidWagonsCount"
              : null;
      if (errorKey) {
        setHasWagonsCountError(true);
        toast.error(t(errorKey));
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSuggestClick = () => {
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const handleWagonsCountChange = (value) => {
    if (isWagonsCountLocked) return;
    setWagonsCount(value);
    setHasWagonsCountError(false);
  };

  const handleConfirmSend = async () => {
    if (!order?.id) return;
    if (!validate()) return;
    setIsSubmitting(true);
    const rounded = Math.round(Number(price));
    const volumeValue = volume === "" ? null : Number(volume);
    const weightValue = weight === "" ? null : Number(weight);

    const data = {
      orderId: order.id,
      price: rounded,
      currency: currency,
      plannedDays: String(transitDays),
      comments: comments,
      volume: volumeValue,
      weight: weightValue,
      wagonsCount: isRailway && Number(wagonsCount) > 0 ? Number(wagonsCount) : null,
    };

    try {
      await Expeditor.sendOrderBids(order.id, data);
      toast.success(t("orderInfoExpeditor.suggestPrice.bidSent"));
      setPrice("");
      setVolume("");
      setWeight("");
      setWagonsCount("");
      setHasWagonsCountError(false);
      setTransitDays("");
      setComments("");
      setShowConfirmModal(false);
      if (onUpdateData) onUpdateData();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.suggestPrice.sendBidError");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = () => setShowRejectModal(true);

  const handleConfirmReject = async () => {
    if (!order?.id) return;
    setIsRejecting(true);
    try {
      await Expeditor.rejectTender(order.id);
      toast.success(t("orderInfoExpeditor.suggestPrice.rejectTenderSuccess"));
      setShowRejectModal(false);
      if (onReject) onReject();
      if (onUpdateData) onUpdateData();
    } catch {
      toast.error(t("orderInfoExpeditor.suggestPrice.rejectTenderError"));
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <div className={style.heading}>
          <span
            className={isAuction ? `${style.topbar_title} ${style.auction_title}` : style.topbar_title}
          >
            {t("orderInfoExpeditor.suggestPrice.title")}
          </span>
          {isAuction && (
            <span className={style.auction_description}>
              {t("orderInfoExpeditor.suggestPrice.auctionDescription")}
            </span>
          )}
        </div>
        {order?.tenderDateEnd && (
          <div className={style.tender_deadline}>
            <span className={style.tender_deadline_label}>
              {t(
                isAuction
                  ? "orderInfoExpeditor.suggestPrice.auctionDeadline"
                  : "orderInfoExpeditor.suggestPrice.tenderDeadline",
              )}
            </span>
            <span className={style.tender_deadline_value}><TenderDeadlineText date={order.tenderDateEnd} /></span>
          </div>
        )}
      </div>

      <div className={style.inputs_row}>
        <div className={style.input_price}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={`${t("orderInfo.suggestPrice.yourPriceLabel")}*`}
            placeholder={t("orderInfo.suggestPrice.placeholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(value) => {
              setPrice(value);
              setErrorText("");
            }}
            onChangeCurrency={(currencyCode) => {
              setCurrency(currencyCode);
            }}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={volume}
            isBgGray
            inputType="number"
            labelText={t("ordersCreateDesktop.detailsSection.volumeLabel")}
            placeholder={initialVolume ? `${initialVolume} ${t("orderInfoExpeditor.suggestPrice.cubicMeter")}` : t("orderInfoExpeditor.suggestPrice.cubicMeter")}
            onChangeValue={setVolume}
          />
        </div>
        <div className={style.input_secondary}>
          <CustomInput
            value={weight}
            isBgGray
            inputType="number"
            labelText={t("orderInfo.ordersInfoCargo.massLabel")}
            placeholder={initialWeight ? `${initialWeight} ${weightUnit}` : weightUnit}
            onChangeValue={setWeight}
          />
        </div>
      </div>
      {lockCurrency && (
        <span className={style.currency_hint}>{t("orderInfoExpeditor.suggestPrice.currencyLockedHint")}</span>
      )}
      <div className={style.inputs_row}>
        <CustomInput
          value={transitDays}
          isBgGray
          inputType="text"
          labelText={`${t("orderInfoExpeditor.suggestPrice.transitDaysLabel")}${transitDaysRequired ? "*" : ""}`}
          placeholder={t("orderInfoExpeditor.suggestPrice.transitDaysPlaceholder")}
          errorText={transitDaysErrorText}
          onChangeValue={(value) => {
            setTransitDays(value.replace(/[^0-9-]/g, ""));
            setTransitDaysErrorText("");
          }}
        />
        {isRailway && (
          <div className={style.wagons_count_field}>
            <div className={counterStyle.vehicles_control}>
              <button
                type="button"
                className={counterStyle.counter_button}
                onClick={() => handleWagonsCountChange(String(Math.max(0, Number(wagonsCount) - 1)))}
                disabled={isWagonsCountLocked || Number(wagonsCount) <= 0 || isSubmitting}
                aria-label={t("railwayOrder.decreaseWagons")}
              >
                -
              </button>
              <div
                className={`${counterStyle.counter_value_box} ${style.wagons_count_box} ${isWagonsCountLocked ? style.wagons_count_locked : ""} ${hasWagonsCountError ? style.wagons_count_error : ""}`}
              >
                <label className={counterStyle.counter_label} htmlFor={wagonsCountInputId}>
                  {t("railwayOrder.wagonsCount")}
                </label>
                <input
                  id={wagonsCountInputId}
                  className={counterStyle.counter_input}
                  type="number"
                  value={Number(wagonsCount) === 0 ? "" : wagonsCount}
                  placeholder={t("railwayOrder.wagonsCountNotSpecified")}
                  min={isWagonsCountRequired ? "1" : "0"}
                  max={MAX_RAILWAY_WAGONS_COUNT}
                  step="1"
                  aria-invalid={hasWagonsCountError}
                  aria-required={isWagonsCountRequired && !isWagonsCountLocked}
                  aria-describedby={isWagonsCountLocked ? `${wagonsCountInputId}-hint` : undefined}
                  disabled={isWagonsCountLocked || isSubmitting}
                  onChange={(event) => handleWagonsCountChange(event.target.value)}
                  onWheel={(event) => event.target.blur()}
                />
              </div>
              <button
                type="button"
                className={counterStyle.counter_button}
                onClick={() =>
                  handleWagonsCountChange(String(Math.min(MAX_RAILWAY_WAGONS_COUNT, Number(wagonsCount) + 1)))
                }
                disabled={isWagonsCountLocked || isSubmitting || Number(wagonsCount) >= MAX_RAILWAY_WAGONS_COUNT}
                aria-label={t("railwayOrder.increaseWagons")}
              >
                +
              </button>
            </div>
            {isWagonsCountLocked && (
              <span id={`${wagonsCountInputId}-hint`} className={style.wagons_count_hint}>
                {t("railwayOrder.wagonsCountLockedHint")}
              </span>
            )}
          </div>
        )}
      </div>
      <div className={style.inputs_row}>
        <CustomTextarea
          value={comments}
          isBgGray
          rows={1}
          labelText={t("orderInfo.suggestPrice.commentLabel")}
          placeholder={t("orderInfoExpeditor.suggestPrice.commentPlaceholder")}
          onChangeValue={setComments}
        />
      </div>

      <div className={style.buttons_row}>
        <div className={style.btn_primary}>
          <CustomButton
            text={t("orderInfo.suggestPrice.suggestButton")}
            isNavy
            onClick={handleSuggestClick}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
        <div className={style.btn_secondary}>
          <CustomButton
            text={t("orderInfoExpeditor.suggestPrice.reject")}
            isNavy
            isWhite
            onClick={handleRejectClick}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <SubmitBidModal
        isOpen={showConfirmModal}
        assignmentMethod={order?.assignmentMethod}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSend}
        isLoading={isSubmitting}
      />

      <ConfirmActionModal
        isOpen={showRejectModal}
        title={t("orderInfoExpeditor.suggestPrice.rejectTenderTitle")}
        message={t("orderInfoExpeditor.suggestPrice.rejectTenderMessage")}
        confirmText={t("orderInfoExpeditor.suggestPrice.rejectTenderConfirm")}
        onConfirm={handleConfirmReject}
        onCancel={() => setShowRejectModal(false)}
        isLoading={isRejecting}
      />
    </div>
  );
};

export default SuggestPriceExpeditor;
